import {getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {
  getSortingKeyAndDirection,
  getSortingOptionLabel,
  type OrderOptions,
} from '@github-ui/dashboard-sorting-dropdown/SortingDropdown'
import {getSignificanceBasedPrecision, human} from '@github-ui/formatters'
import {
  dashboardTypeLabels,
  dashboardTypeLabelSingular,
  type DashboardTypes,
} from '@github-ui/shared-dashboard-sidebar/types/dashboard-types'
import type {EmptyListState, EmptyListMessages} from '@github-ui/shared-dashboard-lists/shared/types/blankslate-types'

export function getScreenReaderMessage(
  dashboardType: DashboardTypes,
  sortingOptions: Record<string, OrderOptions>,
  defaultSortingKey: string,
  getEmptyMessage: (params: EmptyListState) => EmptyListMessages,
  isFetching: boolean,
  currentPage: number,
  query: string,
  resultCount: number,
  pageCount: number,
  resultNoun?: {singular: string; plural: string},
): string {
  if (isFetching) return ''

  if (resultCount === 0) {
    const {heading, description} = getEmptyMessage({
      dashboardType,
      filtered: !!query,
      currentPage,
      pageCount,
    })
    const headingText = heading.endsWith('.') ? heading : `${heading}.`
    return description ? `${headingText} ${description}` : headingText
  }

  const [sortKey, direction] = getSortingKeyAndDirection(getSortFromQuery(query), defaultSortingKey)
  const sortingLabel = getSortingOptionLabel(sortKey, sortingOptions)
  const sortOrder = direction === 'asc' ? 'ascending' : 'descending'

  return `${getDisplayCount(dashboardType, resultCount, resultNoun)} found. List is sorted by ${sortingLabel} in ${sortOrder} order.`
}

export function getDisplayCount(
  dashboardType: DashboardTypes,
  count: number,
  resultNoun?: {singular: string; plural: string},
): string {
  const displayCount = human(count, {
    precision: getSignificanceBasedPrecision(count),
  })

  const noun = resultNoun
    ? count === 1
      ? resultNoun.singular
      : resultNoun.plural
    : count === 1
      ? dashboardTypeLabelSingular[dashboardType]
      : dashboardTypeLabels[dashboardType]

  return `${displayCount} ${noun}`
}
